import { Value } from 'firebase-admin/remote-config';
import { z } from 'zod';

/**
 * Helpers to write Zod Schemas that operate on Firebase Remote Config Value objects
 */

/**
 * Validates that the supplied input is a Firebase Remote Config Value object, regardless of what it contains
 */
const firebaseValueSchema = z.custom<Value>(val => {
  return (
    typeof val === 'object' && val !== null && 'getSource' in val && (val as Value).getSource() !== undefined
  );
});

/**
 * Parses a Firebase Remote Config Value object as JSON and returns the resulting object.
 *
 * This can be piped into other Zod schemas to validate the resulting object, e.g.:
 * ```ts
 * const myJsonConfigSchema = jsonValue.pipe(z.object({ ... }));
 * ```
 */
export const jsonValue = firebaseValueSchema
  .transform(val => val.asString())
  .transform((str, ctx) => {
    try {
      return JSON.parse(str) as unknown;
    } catch (e) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Failed to parse Firebase Remote Config value as JSON: ${(e as Error).message}`,
      });
      return z.NEVER;
    }
  });

/**
 * Parses a Firebase Remote Config Value object as a string
 */
export const stringValue = firebaseValueSchema.transform(val => val.asString()).pipe(z.string());
/**
 * Parses a Firebase Remote Config Value object as a number
 */
export const numberValue = firebaseValueSchema.transform(val => val.asNumber()).pipe(z.number());

/**
 * Parses a Firebase Remote Config Value object as a boolean
 */
export const boolValue = firebaseValueSchema.transform(val => val.asBoolean()).pipe(z.boolean());
